export const ROOT_ID = 'root';

export type Id = string;
export type Side = 1 | -1;
export type OrigamiRuntimeMode = 'static-view' | 'interactive-bridge' | 'baked-view';

export interface Point2 {
  x: number;
  y: number;
}

export interface PaperSpec {
  width: number;
  height: number;
}

export interface CameraSpec {
  perspective: number;
  perspectiveOrigin: Point2;
}

export interface HingeLine {
  a: Point2;
  b: Point2;
}

export interface FoldOp {
  id: Id;
  targetNodeId: Id;
  childNodeId: Id;
  line: HingeLine;
  movingSide: Side;
  angleDeg: number;
  disabled?: boolean;
}

export interface ControlLine {
  id: Id;
  targetNodeId: Id;
  a: Point2;
  b: Point2;
  movingSide: Side;
  angleDeg: number;
  opId?: Id;
  status: 'draft' | 'committed' | 'invalid';
}

export interface OrigamiDocumentState {
  paper: PaperSpec;
  camera: CameraSpec;
  foldOps: FoldOp[];
  controls: Record<Id, ControlLine>;
  selectedNodeId?: Id;
}

export interface PreCrease {
  id: Id;
  targetNodeId: Id;
  line: HingeLine;
}

export type FoldCorner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface FoldPlanInspectionWarning {
  code: 'large-root-active-fold' | 'invalid-fold-op' | 'tiny-fold-face';
  severity: 'warning' | 'error';
  opId: Id;
  message: string;
}

export interface FoldPlanInspection {
  tree: DerivedFoldTree;
  warnings: FoldPlanInspectionWarning[];
}

export type Polygon = Point2[];

export type Mat4 = [
  number, number, number, number,
  number, number, number, number,
  number, number, number, number,
  number, number, number, number
];

export interface Point3 {
  x: number;
  y: number;
  z: number;
}

export interface FoldNode {
  id: Id;
  parentId: Id | null;
  polygon: Polygon;
  projectedPolygon: Polygon;
  hinge: HingeLine | null;
  angleDeg: number;
  sourceOpId: Id | null;
  children: Id[];
  localMatrix: Mat4;
  worldMatrix: Mat4;
  depth: number;
  valid: boolean;
}

export interface DerivedFoldTree {
  rootId: Id;
  nodes: Record<Id, FoldNode>;
  renderOrder: Id[];
  invalidOps: Record<Id, string>;
}

export interface Snapshot {
  id: Id;
  width: number;
  height: number;
  url: string;
  revoke?: () => void;
}

export interface SnapshotProvider {
  capture(sourceRoot: HTMLElement, paper: PaperSpec): Promise<Snapshot>;
}

export interface FoldVisualPiece {
  nodeId: Id;
  polygon: Polygon;
  clipPath: string;
  transform: string;
  backgroundPosition: string;
  backgroundSize: string;
}

export interface BakedOrigamiManifest {
  mode: 'baked-view';
  paper: PaperSpec;
  camera: CameraSpec;
  snapshot: Snapshot;
  pieces: FoldVisualPiece[];
}

export interface FoldHit {
  nodeId: Id;
  localPoint: Point2;
}

export interface MappedEventTarget {
  originalEvent: { clientX: number; clientY: number; type: string };
  hit: FoldHit;
  sourcePoint: Point2;
  sourceTarget: HTMLElement;
  elementId?: Id;
}

export interface ActivePointerState {
  pointerId: number;
  sourceTarget: HTMLElement;
  nodeId: Id;
  startLocal: Point2;
  lastLocal: Point2;
  buttons: number;
}

export interface InteractionAdapter {
  name: string;
  match(el: HTMLElement): boolean;
  pointerDown?(ctx: MappedEventTarget): boolean;
  pointerMove?(ctx: MappedEventTarget, active?: ActivePointerState): boolean;
  pointerUp?(ctx: MappedEventTarget, active?: ActivePointerState): boolean;
}

export interface StaticViewRuntimeOptions {
  mode: 'static-view';
  host: HTMLElement;
  paper: PaperSpec;
  snapshot: Snapshot;
  foldOps: FoldOp[];
  camera?: Partial<CameraSpec>;
}

export interface InteractiveVisualOptions {
  backend?: 'snapshot' | 'live-mirror';
  pseudoStates?: {
    hover?: boolean;
    active?: boolean;
    focus?: boolean;
    focusVisible?: boolean;
  };
}

export interface InteractiveRuntimeOptions {
  mode: 'interactive-bridge';
  host: HTMLElement;
  sourceRoot: HTMLElement;
  paper: PaperSpec;
  foldOps?: FoldOp[];
  snapshotProvider: SnapshotProvider;
  adapters?: InteractionAdapter[];
  visual?: InteractiveVisualOptions;
  camera?: Partial<CameraSpec>;
}

export interface BakedViewRuntimeOptions {
  mode: 'baked-view';
  host: HTMLElement;
  manifest: BakedOrigamiManifest;
}

export type OrigamiRuntimeOptions = StaticViewRuntimeOptions | InteractiveRuntimeOptions | BakedViewRuntimeOptions;

export interface OrigamiRuntime {
  readonly mode: OrigamiRuntimeMode;
  render(): void;
  mount(): Promise<void>;
  setAngle(opId: Id, angleDeg: number): boolean;
  setAngles?(updates: Array<{ opId: Id; angleDeg: number }>): boolean;
  setMode?(mode: 'static-view' | 'interactive-bridge'): void;
  bridgePointer?(event: { clientX: number; clientY: number; type: string }): boolean;
  dispose(): void;
}

function defaultCamera(paper: PaperSpec, camera?: Partial<CameraSpec>): CameraSpec {
  return {
    perspective: camera?.perspective ?? 900,
    perspectiveOrigin: camera?.perspectiveOrigin ?? { x: paper.width / 2, y: paper.height / 2 }
  };
}

function cloneFoldOps(foldOps: FoldOp[]): FoldOp[] {
  return foldOps.map((op) => ({
    ...op,
    line: {
      a: { ...op.line.a },
      b: { ...op.line.b }
    }
  }));
}

function rootPolygon(paper: PaperSpec): Polygon {
  return [
    { x: 0, y: 0 },
    { x: paper.width, y: 0 },
    { x: paper.width, y: paper.height },
    { x: 0, y: paper.height }
  ];
}

function polygonArea(polygon: Polygon): number {
  let area = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area) / 2;
}

function cloneLine(line: HingeLine): HingeLine {
  return { a: { ...line.a }, b: { ...line.b } };
}

function defaultChildName(id: string): string {
  return id.startsWith('fold-') ? `${id.slice(5)}-panel` : `${id}-panel`;
}

function cornerGeometry(paper: PaperSpec, corner: FoldCorner, size: number): { line: HingeLine; movingSide: Side; childId: Id } {
  const width = paper.width;
  const height = paper.height;
  switch (corner) {
    case 'top-right':
      return {
        line: { a: { x: width - size, y: 0 }, b: { x: width, y: size } },
        movingSide: 1,
        childId: 'top-right-flap'
      };
    case 'top-left':
      return {
        line: { a: { x: size, y: 0 }, b: { x: 0, y: size } },
        movingSide: -1,
        childId: 'top-left-flap'
      };
    case 'bottom-right':
      return {
        line: { a: { x: width - size, y: height }, b: { x: width, y: height - size } },
        movingSide: -1,
        childId: 'bottom-right-flap'
      };
    case 'bottom-left':
      return {
        line: { a: { x: 0, y: height - size }, b: { x: size, y: height } },
        movingSide: -1,
        childId: 'bottom-left-flap'
      };
  }
}

export class FoldPlanBuilder {
  readonly paper: PaperSpec;
  readonly foldOps: FoldOp[] = [];
  readonly creases: PreCrease[] = [];

  constructor(paper: PaperSpec) {
    this.paper = { ...paper };
  }

  preCrease(id: Id, line: HingeLine, options: { target?: Id } = {}): this {
    this.creases.push({ id, targetNodeId: options.target ?? ROOT_ID, line: cloneLine(line) });
    return this;
  }

  foldRight(options: { id?: Id; childId?: Id; target?: Id; x: number; angle: number }): this {
    const id = options.id ?? 'fold-right';
    this.foldOps.push({
      id,
      targetNodeId: options.target ?? ROOT_ID,
      childNodeId: options.childId ?? defaultChildName(id),
      line: { a: { x: options.x, y: 0 }, b: { x: options.x, y: this.paper.height } },
      movingSide: 1,
      angleDeg: options.angle
    });
    return this;
  }

  foldLeft(options: { id?: Id; childId?: Id; target?: Id; x: number; angle: number }): this {
    const id = options.id ?? 'fold-left';
    this.foldOps.push({
      id,
      targetNodeId: options.target ?? ROOT_ID,
      childNodeId: options.childId ?? defaultChildName(id),
      line: { a: { x: options.x, y: 0 }, b: { x: options.x, y: this.paper.height } },
      movingSide: -1,
      angleDeg: options.angle
    });
    return this;
  }

  foldCorner(options: { corner: FoldCorner; size: number; angle: number; id?: Id; childId?: Id; target?: Id }): this {
    const id = options.id ?? `fold-${options.corner}`;
    const geometry = cornerGeometry(this.paper, options.corner, options.size);
    this.foldOps.push({
      id,
      targetNodeId: options.target ?? ROOT_ID,
      childNodeId: options.childId ?? geometry.childId,
      line: geometry.line,
      movingSide: geometry.movingSide,
      angleDeg: options.angle
    });
    return this;
  }

  zFold(options: { axis?: 'vertical'; angles: [number, number]; ids?: [Id, Id]; childIds?: [Id, Id] }): this {
    const firstId = options.ids?.[0] ?? 'fold-first-third';
    const secondId = options.ids?.[1] ?? 'fold-last-third';
    const firstChild = options.childIds?.[0] ?? 'first-third-panel';
    const secondChild = options.childIds?.[1] ?? 'last-third-panel';
    const third = this.paper.width / 3;
    this.foldOps.push({
      id: firstId,
      targetNodeId: ROOT_ID,
      childNodeId: firstChild,
      line: { a: { x: third, y: 0 }, b: { x: third, y: this.paper.height } },
      movingSide: -1,
      angleDeg: options.angles[0]
    });
    this.foldOps.push({
      id: secondId,
      targetNodeId: ROOT_ID,
      childNodeId: secondChild,
      line: { a: { x: third * 2, y: 0 }, b: { x: third * 2, y: this.paper.height } },
      movingSide: 1,
      angleDeg: options.angles[1]
    });
    return this;
  }

  toDocumentState(camera?: Partial<CameraSpec>): OrigamiDocumentState {
    const controls = Object.fromEntries(this.creases.map((crease) => [
      crease.id,
      {
        id: crease.id,
        targetNodeId: crease.targetNodeId,
        a: { ...crease.line.a },
        b: { ...crease.line.b },
        movingSide: 1 as Side,
        angleDeg: 0,
        status: 'draft' as const
      }
    ]));
    return {
      paper: { ...this.paper },
      camera: defaultCamera(this.paper, camera),
      foldOps: cloneFoldOps(this.foldOps),
      controls
    };
  }
}

export function createFoldPlan(paper: PaperSpec): FoldPlanBuilder {
  return new FoldPlanBuilder(paper);
}

function signedDistanceToLine(point: Point2, line: HingeLine): number {
  const dx = line.b.x - line.a.x;
  const dy = line.b.y - line.a.y;
  return dy * (point.x - line.a.x) - dx * (point.y - line.a.y);
}

function clipPolygonByLine(polygon: Polygon, line: HingeLine, side: Side): Polygon {
  const signed = (point: Point2) => signedDistanceToLine(point, line);
  const inside = (point: Point2) => side === 1 ? signed(point) >= -1e-9 : signed(point) <= 1e-9;
  const intersect = (a: Point2, b: Point2): Point2 => {
    const da = signed(a);
    const db = signed(b);
    const t = Math.abs(da - db) < 1e-9 ? 0 : da / (da - db);
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t
    };
  };

  const output: Polygon = [];
  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const previous = polygon[(index + polygon.length - 1) % polygon.length];
    const currentInside = inside(current);
    const previousInside = inside(previous);

    if (currentInside) {
      if (!previousInside) output.push(intersect(previous, current));
      output.push(current);
    } else if (previousInside) {
      output.push(intersect(previous, current));
    }
  }

  return dedupePolygon(output);
}

function dedupePolygon(polygon: Polygon): Polygon {
  const result: Polygon = [];
  for (const point of polygon) {
    const last = result.at(-1);
    if (!last || Math.abs(last.x - point.x) > 1e-6 || Math.abs(last.y - point.y) > 1e-6) {
      result.push(roundPoint(point));
    }
  }
  if (result.length > 1) {
    const first = result[0];
    const last = result.at(-1)!;
    if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) result.pop();
  }
  return result;
}

function roundPoint(point: Point2): Point2 {
  return {
    x: Math.abs(point.x) < 1e-9 ? 0 : Number(point.x.toFixed(6)),
    y: Math.abs(point.y) < 1e-9 ? 0 : Number(point.y.toFixed(6))
  };
}

export function mat4Identity(): Mat4 {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

function mat4Translation(x: number, y: number, z: number): Mat4 {
  return [1, 0, 0, x, 0, 1, 0, y, 0, 0, 1, z, 0, 0, 0, 1];
}

function mat4AxisRotation(axis: Point3, angleDeg: number): Mat4 {
  const length = Math.hypot(axis.x, axis.y, axis.z) || 1;
  const x = axis.x / length;
  const y = axis.y / length;
  const z = axis.z / length;
  const radians = angleDeg * Math.PI / 180;
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  const t = 1 - c;
  return [
    t * x * x + c, t * x * y - s * z, t * x * z + s * y, 0,
    t * x * y + s * z, t * y * y + c, t * y * z - s * x, 0,
    t * x * z - s * y, t * y * z + s * x, t * z * z + c, 0,
    0, 0, 0, 1
  ];
}

export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const out = Array.from({ length: 16 }, () => 0) as Mat4;
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      out[row * 4 + col] = a[row * 4 + 0] * b[col + 0]
        + a[row * 4 + 1] * b[col + 4]
        + a[row * 4 + 2] * b[col + 8]
        + a[row * 4 + 3] * b[col + 12];
    }
  }
  return out.map((value) => Math.abs(value) < 1e-12 ? 0 : Number(value.toFixed(12))) as Mat4;
}

export function mat4ApplyPoint(matrix: Mat4, point: Point3): Point3 {
  const x = matrix[0] * point.x + matrix[1] * point.y + matrix[2] * point.z + matrix[3];
  const y = matrix[4] * point.x + matrix[5] * point.y + matrix[6] * point.z + matrix[7];
  const z = matrix[8] * point.x + matrix[9] * point.y + matrix[10] * point.z + matrix[11];
  const w = matrix[12] * point.x + matrix[13] * point.y + matrix[14] * point.z + matrix[15];
  return { x: x / (w || 1), y: y / (w || 1), z: z / (w || 1) };
}

export function mat4Invert(matrix: Mat4): Mat4 {
  const r00 = matrix[0];
  const r01 = matrix[1];
  const r02 = matrix[2];
  const r10 = matrix[4];
  const r11 = matrix[5];
  const r12 = matrix[6];
  const r20 = matrix[8];
  const r21 = matrix[9];
  const r22 = matrix[10];
  const tx = matrix[3];
  const ty = matrix[7];
  const tz = matrix[11];

  return [
    r00, r10, r20, -(r00 * tx + r10 * ty + r20 * tz),
    r01, r11, r21, -(r01 * tx + r11 * ty + r21 * tz),
    r02, r12, r22, -(r02 * tx + r12 * ty + r22 * tz),
    0, 0, 0, 1
  ].map((value) => Math.abs(value) < 1e-12 ? 0 : Number(value.toFixed(12))) as Mat4;
}

export function cssMatrixFromMat4(matrix: Mat4): string {
  const cssOrder = [0, 4, 8, 12, 1, 5, 9, 13, 2, 6, 10, 14, 3, 7, 11, 15];
  return `matrix3d(${cssOrder.map((index) => formatCssNumber(matrix[index])).join(', ')})`;
}

function formatCssNumber(value: number): string {
  return String(Math.abs(value) < 1e-12 ? 0 : Number(value.toFixed(12)));
}

function foldTransform(line: HingeLine, angleDeg: number): Mat4 {
  const axis = { x: line.b.x - line.a.x, y: line.b.y - line.a.y, z: 0 };
  return mat4Multiply(
    mat4Multiply(mat4Translation(line.a.x, line.a.y, 0), mat4AxisRotation(axis, angleDeg)),
    mat4Translation(-line.a.x, -line.a.y, 0)
  );
}

export function buildDerivedFoldTree(documentState: OrigamiDocumentState): DerivedFoldTree {
  const nodes: Record<Id, FoldNode> = {
    [ROOT_ID]: {
      id: ROOT_ID,
      parentId: null,
      polygon: rootPolygon(documentState.paper),
      projectedPolygon: [],
      hinge: null,
      angleDeg: 0,
      sourceOpId: null,
      children: [],
      localMatrix: mat4Identity(),
      worldMatrix: mat4Identity(),
      depth: 0,
      valid: true
    }
  };
  const invalidOps: Record<Id, string> = {};

  for (const op of documentState.foldOps) {
    if (op.disabled) continue;
    const target = nodes[op.targetNodeId];
    if (!target) {
      invalidOps[op.id] = `Missing target node: ${op.targetNodeId}`;
      continue;
    }

    const childPolygon = clipPolygonByLine(target.polygon, op.line, op.movingSide);
    const remainingPolygon = clipPolygonByLine(target.polygon, op.line, op.movingSide === 1 ? -1 : 1);
    if (childPolygon.length < 3 || remainingPolygon.length < 3) {
      invalidOps[op.id] = 'Fold line does not split target polygon';
      continue;
    }

    target.polygon = remainingPolygon;
    target.children.push(op.childNodeId);
    const localMatrix = foldTransform(op.line, op.angleDeg);
    nodes[op.childNodeId] = {
      id: op.childNodeId,
      parentId: target.id,
      polygon: childPolygon,
      projectedPolygon: [],
      hinge: op.line,
      angleDeg: op.angleDeg,
      sourceOpId: op.id,
      children: [],
      localMatrix,
      worldMatrix: mat4Multiply(target.worldMatrix, localMatrix),
      depth: target.depth + 1,
      valid: true
    };
  }

  for (const node of Object.values(nodes)) {
    node.projectedPolygon = transformedPolygon(node);
  }

  return {
    rootId: ROOT_ID,
    nodes,
    renderOrder: Object.values(nodes).sort((a, b) => a.depth - b.depth).map((node) => node.id),
    invalidOps
  };
}

export function inspectFoldPlan(options: {
  paper: PaperSpec;
  foldOps: FoldOp[];
  camera?: Partial<CameraSpec>;
}): FoldPlanInspection {
  const camera = defaultCamera(options.paper, options.camera);
  const tree = buildDerivedFoldTree({ paper: options.paper, camera, foldOps: cloneFoldOps(options.foldOps), controls: {} });
  const warnings: FoldPlanInspectionWarning[] = [];

  for (const [opId, message] of Object.entries(tree.invalidOps)) {
    warnings.push({ code: 'invalid-fold-op', severity: 'error', opId, message });
  }

  const paperArea = options.paper.width * options.paper.height;
  const firstActiveRootFold = options.foldOps.find((op) => !op.disabled && op.targetNodeId === ROOT_ID && !tree.invalidOps[op.id]);
  if (firstActiveRootFold && Math.abs(firstActiveRootFold.angleDeg) >= 35) {
    const child = tree.nodes[firstActiveRootFold.childNodeId];
    const childAreaRatio = child ? polygonArea(child.polygon) / paperArea : 0;
    if (childAreaRatio >= 0.4 && childAreaRatio <= 0.6) {
      warnings.push({
        code: 'large-root-active-fold',
        severity: 'warning',
        opId: firstActiveRootFold.id,
        message: `The first active fold rotates ${(childAreaRatio * 100).toFixed(0)}% of the root sheet. This can read like the paper was cut in half; use a pre-crease at 0° or target a smaller local flap when that is the intended visual.`
      });
    }
  }

  for (const op of options.foldOps) {
    const child = tree.nodes[op.childNodeId];
    if (!child) continue;
    const areaRatio = polygonArea(child.polygon) / paperArea;
    if (areaRatio > 0 && areaRatio < 0.015) {
      warnings.push({
        code: 'tiny-fold-face',
        severity: 'warning',
        opId: op.id,
        message: `Fold '${op.id}' creates a very small face (${(areaRatio * 100).toFixed(1)}% of the paper). It may alias or disappear in CSS 3D; increase flap size or simplify the fold.`
      });
    }
  }

  return { tree, warnings };
}

function polygonToClipPath(polygon: Polygon): string {
  return `polygon(${polygon.map((point) => `${point.x}px ${point.y}px`).join(', ')})`;
}

function polygonToBleedClipPath(polygon: Polygon, bleedPx: number): string {
  if (bleedPx <= 0 || polygon.length === 0) return polygonToClipPath(polygon);
  const centroid = polygon.reduce(
    (acc, point) => ({ x: acc.x + point.x / polygon.length, y: acc.y + point.y / polygon.length }),
    { x: 0, y: 0 }
  );
  const expanded = polygon.map((point) => {
    const dx = point.x - centroid.x;
    const dy = point.y - centroid.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) return point;
    return {
      x: point.x + dx / length * bleedPx,
      y: point.y + dy / length * bleedPx
    };
  });
  return polygonToClipPath(expanded);
}

function pointOnSegment(point: Point2, a: Point2, b: Point2): boolean {
  const cross = (point.y - a.y) * (b.x - a.x) - (point.x - a.x) * (b.y - a.y);
  if (Math.abs(cross) > 1e-6) return false;
  const dot = (point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y);
  if (dot < -1e-6) return false;
  const lengthSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  return dot <= lengthSq + 1e-6;
}

function pointInPolygon(point: Point2, polygon: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (pointOnSegment(point, pj, pi)) return true;
    const crosses = (pi.y > point.y) !== (pj.y > point.y)
      && point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y || 1e-12) + pi.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function transformedPolygon(node: FoldNode): Polygon {
  return node.polygon.map((point) => {
    const transformed = mat4ApplyPoint(node.worldMatrix, { x: point.x, y: point.y, z: 0 });
    return roundPoint({ x: transformed.x, y: transformed.y });
  });
}

function barycentric(point: Point2, a: Point2, b: Point2, c: Point2): [number, number, number] | null {
  const v0 = { x: b.x - a.x, y: b.y - a.y };
  const v1 = { x: c.x - a.x, y: c.y - a.y };
  const v2 = { x: point.x - a.x, y: point.y - a.y };
  const d00 = v0.x * v0.x + v0.y * v0.y;
  const d01 = v0.x * v1.x + v0.y * v1.y;
  const d11 = v1.x * v1.x + v1.y * v1.y;
  const d20 = v2.x * v0.x + v2.y * v0.y;
  const d21 = v2.x * v1.x + v2.y * v1.y;
  const denom = d00 * d11 - d01 * d01;
  if (Math.abs(denom) < 1e-9) return null;
  const v = (d11 * d20 - d01 * d21) / denom;
  const w = (d00 * d21 - d01 * d20) / denom;
  const u = 1 - v - w;
  return [u, v, w];
}

function weightsInside(weights: [number, number, number]): boolean {
  return weights.every((value) => value >= -1e-6 && value <= 1 + 1e-6);
}

function mapProjectedPointToSource(point: Point2, node: FoldNode): Point2 {
  const projected = node.projectedPolygon.length > 0 ? node.projectedPolygon : transformedPolygon(node);
  for (let index = 1; index < projected.length - 1; index += 1) {
    const weights = barycentric(point, projected[0], projected[index], projected[index + 1]);
    if (!weights || !weightsInside(weights)) continue;
    const sourceA = node.polygon[0];
    const sourceB = node.polygon[index];
    const sourceC = node.polygon[index + 1];
    return {
      x: sourceA.x * weights[0] + sourceB.x * weights[1] + sourceC.x * weights[2],
      y: sourceA.y * weights[0] + sourceB.y * weights[1] + sourceC.y * weights[2]
    };
  }
  return point;
}

export function hitTestFoldTree(stagePoint: Point2, tree: DerivedFoldTree): FoldHit | null {
  for (const nodeId of [...tree.renderOrder].reverse()) {
    const node = tree.nodes[nodeId];
    if (pointInPolygon(stagePoint, node.projectedPolygon)) {
      return { nodeId, localPoint: mapProjectedPointToSource(stagePoint, node) };
    }
  }
  return null;
}

function piecesFromTree(tree: DerivedFoldTree, paper: PaperSpec): FoldVisualPiece[] {
  return tree.renderOrder.map((nodeId) => {
    const node = tree.nodes[nodeId];
    return {
      nodeId,
      polygon: node.polygon,
      clipPath: polygonToClipPath(node.polygon),
      transform: cssMatrixFromMat4(node.worldMatrix),
      backgroundPosition: '0px 0px',
      backgroundSize: `${paper.width}px ${paper.height}px`
    } satisfies FoldVisualPiece;
  });
}

export function buildBakedOrigamiManifest(options: {
  paper: PaperSpec;
  snapshot: Snapshot;
  foldOps: FoldOp[];
  camera?: Partial<CameraSpec>;
}): BakedOrigamiManifest {
  const camera = defaultCamera(options.paper, options.camera);
  const state: OrigamiDocumentState = { paper: options.paper, camera, foldOps: cloneFoldOps(options.foldOps), controls: {} };
  const tree = buildDerivedFoldTree(state);
  return {
    mode: 'baked-view',
    paper: options.paper,
    camera,
    snapshot: options.snapshot,
    pieces: piecesFromTree(tree, options.paper)
  };
}

class FoldVisualRenderer {
  readonly rootElement: HTMLElement;
  private snapshot: Snapshot | null = null;

  constructor(rootElement: HTMLElement) {
    this.rootElement = rootElement;
  }

  setSnapshot(snapshot: Snapshot): void {
    this.snapshot?.revoke?.();
    this.snapshot = snapshot;
  }

  renderPieces(pieces: FoldVisualPiece[], paper: PaperSpec, baked: boolean): void {
    if (!this.snapshot) return;
    this.rootElement.innerHTML = '';
    this.rootElement.style.position = 'relative';
    this.rootElement.style.width = `${paper.width}px`;
    this.rootElement.style.height = `${paper.height}px`;
    this.rootElement.style.transformStyle = 'preserve-3d';

    for (const piece of pieces) {
      const node = document.createElement('div');
      node.className = 'ori-fold-node';
      node.dataset.oriNodeId = piece.nodeId;
      node.style.position = 'absolute';
      node.style.inset = '0';
      node.style.transformOrigin = '0 0';
      node.style.transformStyle = 'preserve-3d';
      node.style.pointerEvents = 'none';
      node.style.transform = piece.transform;

      const paint = document.createElement('div');
      paint.className = 'ori-fold-paint';
      paint.style.position = 'absolute';
      paint.style.inset = '0';
      paint.style.pointerEvents = 'none';
      paint.style.backfaceVisibility = 'visible';
      paint.style.backgroundImage = `url("${this.snapshot.url}")`;
      paint.style.backgroundPosition = piece.backgroundPosition;
      paint.style.backgroundSize = piece.backgroundSize;
      paint.style.backgroundRepeat = 'no-repeat';
      paint.style.clipPath = piece.clipPath;
      paint.dataset.oriBaked = String(baked);

      node.appendChild(paint);
      this.rootElement.appendChild(node);
    }
  }
}

export class FoldElementKeyRegistry {
  private nextId = 1;
  private readonly keyByElement = new WeakMap<Element, string>();

  assign(root: HTMLElement): void {
    this.ensureKey(root);
    const elements = root.querySelectorAll<HTMLElement>('*');
    for (const element of elements) this.ensureKey(element);
  }

  ensureKey(element: HTMLElement): string {
    const existing = this.keyByElement.get(element) ?? element.dataset.foldKey;
    if (existing) {
      this.keyByElement.set(element, existing);
      element.dataset.foldKey = existing;
      return existing;
    }
    const key = `fold_el_${this.nextId++}`;
    this.keyByElement.set(element, key);
    element.dataset.foldKey = key;
    return key;
  }

  getKey(element: HTMLElement): string | undefined {
    return this.keyByElement.get(element) ?? element.dataset.foldKey;
  }
}

interface MirrorFragmentDom {
  fragmentEl: HTMLElement;
  clipEl: HTMLElement;
  mirrorRoot: HTMLElement;
  keyToCloneEl: Map<string, HTMLElement>;
}

class LiveMirrorRenderer {
  readonly rootElement: HTMLElement;
  private readonly fragments = new Map<Id, MirrorFragmentDom>();

  constructor(
    rootElement: HTMLElement,
    private readonly sourceRoot: HTMLElement,
    private readonly keyRegistry: FoldElementKeyRegistry
  ) {
    this.rootElement = rootElement;
  }

  renderPieces(pieces: FoldVisualPiece[], paper: PaperSpec): void {
    this.keyRegistry.assign(this.sourceRoot);
    this.rootElement.style.position = 'relative';
    this.rootElement.style.width = `${paper.width}px`;
    this.rootElement.style.height = `${paper.height}px`;
    this.rootElement.style.transformStyle = 'preserve-3d';

    const seen = new Set<Id>();
    for (const piece of pieces) {
      seen.add(piece.nodeId);
      const dom = this.ensureFragment(piece.nodeId);
      dom.fragmentEl.dataset.oriNodeId = piece.nodeId;
      dom.fragmentEl.style.transform = piece.transform;
      dom.clipEl.style.clipPath = polygonToBleedClipPath(piece.polygon, 2.5);
      this.rootElement.appendChild(dom.fragmentEl);
    }

    for (const [id, dom] of this.fragments) {
      if (!seen.has(id)) {
        dom.fragmentEl.remove();
        this.fragments.delete(id);
      }
    }
  }

  syncSourceMutation(): void {
    for (const [id, dom] of this.fragments) {
      const mirror = this.createMirrorRoot();
      dom.mirrorRoot.replaceWith(mirror.root);
      dom.mirrorRoot = mirror.root;
      dom.keyToCloneEl = mirror.map;
      this.fragments.set(id, dom);
    }
  }

  setPseudoState(params: { key?: string; hover?: boolean; active?: boolean; focus?: boolean; focusVisible?: boolean }): void {
    for (const dom of this.fragments.values()) {
      for (const element of dom.keyToCloneEl.values()) {
        if (params.hover !== undefined) delete element.dataset.foldHover;
        if (params.active !== undefined) delete element.dataset.foldActive;
        if (params.focus !== undefined) delete element.dataset.foldFocus;
        if (params.focusVisible !== undefined) delete element.dataset.foldFocusVisible;
      }

      if (!params.key) continue;
      const clone = dom.keyToCloneEl.get(params.key);
      if (!clone) continue;
      if (params.hover) clone.dataset.foldHover = 'true';
      if (params.active) clone.dataset.foldActive = 'true';
      if (params.focus) clone.dataset.foldFocus = 'true';
      if (params.focusVisible) clone.dataset.foldFocusVisible = 'true';
    }
  }

  mirrorFormValues(): void {
    const controls = this.sourceRoot.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select');
    for (const sourceControl of controls) {
      const key = this.keyRegistry.getKey(sourceControl);
      if (!key) continue;
      for (const dom of this.fragments.values()) {
        const clone = dom.keyToCloneEl.get(key);
        if (clone instanceof HTMLInputElement && sourceControl instanceof HTMLInputElement) {
          clone.value = sourceControl.value;
          clone.checked = sourceControl.checked;
        } else if (clone instanceof HTMLTextAreaElement && sourceControl instanceof HTMLTextAreaElement) {
          clone.value = sourceControl.value;
        } else if (clone instanceof HTMLSelectElement && sourceControl instanceof HTMLSelectElement) {
          clone.selectedIndex = sourceControl.selectedIndex;
        }
      }
    }
  }

  private ensureFragment(id: Id): MirrorFragmentDom {
    const existing = this.fragments.get(id);
    if (existing) return existing;

    const fragmentEl = document.createElement('div');
    fragmentEl.className = 'ori-live-fragment ori-fold-node';
    fragmentEl.style.position = 'absolute';
    fragmentEl.style.inset = '0';
    fragmentEl.style.transformOrigin = '0 0';
    fragmentEl.style.transformStyle = 'preserve-3d';
    fragmentEl.style.pointerEvents = 'none';
    fragmentEl.style.overflow = 'visible';

    const clipEl = document.createElement('div');
    clipEl.className = 'ori-live-clip';
    clipEl.style.position = 'absolute';
    clipEl.style.inset = '0';
    clipEl.style.pointerEvents = 'none';
    clipEl.style.backfaceVisibility = 'visible';

    const mirror = this.createMirrorRoot();
    clipEl.appendChild(mirror.root);
    fragmentEl.appendChild(clipEl);

    const dom = { fragmentEl, clipEl, mirrorRoot: mirror.root, keyToCloneEl: mirror.map };
    this.fragments.set(id, dom);
    return dom;
  }

  private createMirrorRoot(): { root: HTMLElement; map: Map<string, HTMLElement> } {
    const clone = this.sourceRoot.cloneNode(true) as HTMLElement;
    clone.classList.add('ori-live-mirror');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.style.pointerEvents = 'none';
    clone.style.userSelect = 'none';
    sanitizeDuplicateIds(clone);
    return { root: clone, map: buildCloneElementMap(clone) };
  }
}

function buildCloneElementMap(root: HTMLElement): Map<string, HTMLElement> {
  const map = new Map<string, HTMLElement>();
  const key = root.dataset.foldKey;
  if (key) map.set(key, root);
  for (const element of root.querySelectorAll<HTMLElement>('[data-fold-key]')) {
    const elementKey = element.dataset.foldKey;
    if (elementKey) map.set(elementKey, element);
  }
  return map;
}

function sanitizeDuplicateIds(root: HTMLElement): void {
  if (root.id) {
    root.dataset.foldOriginalId = root.id;
    root.removeAttribute('id');
  }
  for (const element of root.querySelectorAll<HTMLElement>('[id]')) {
    element.dataset.foldOriginalId = element.id;
    element.removeAttribute('id');
  }
}

class SourceSurface {
  constructor(readonly sourceRoot: HTMLElement) {}

  elementFromLocalPoint(point: Point2): HTMLElement | null {
    const candidates = Array.from(this.sourceRoot.querySelectorAll<HTMLElement>('*')).reverse();
    for (const element of candidates) {
      const box = readLocalBox(element);
      if (box && point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height) {
        return element;
      }
    }
    return this.sourceRoot;
  }

  dispose(): void {}
}

function readLocalBox(element: HTMLElement): { x: number; y: number; width: number; height: number } | null {
  const style = getComputedStyle(element);
  const left = parseCssPx(element.style.left || style.left);
  const top = parseCssPx(element.style.top || style.top);
  const width = parseCssPx(element.style.width || style.width);
  const height = parseCssPx(element.style.height || style.height);
  if (width > 0 && height > 0) return { x: left, y: top, width, height };

  const rect = element.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
  return null;
}

function parseCssPx(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export class StaticImageSnapshotProvider implements SnapshotProvider {
  constructor(private readonly snapshot: Snapshot) {}

  async capture(): Promise<Snapshot> {
    return this.snapshot;
  }
}

export class ButtonAdapter implements InteractionAdapter {
  name = 'ButtonAdapter';

  match(el: HTMLElement): boolean {
    return el instanceof HTMLButtonElement
      || (el instanceof HTMLInputElement && ['button', 'submit', 'checkbox', 'radio'].includes(el.type));
  }

  pointerUp(ctx: MappedEventTarget): boolean {
    ctx.sourceTarget.click();
    return true;
  }
}

export class AnchorAdapter implements InteractionAdapter {
  name = 'AnchorAdapter';

  match(el: HTMLElement): boolean {
    return el instanceof HTMLAnchorElement;
  }

  pointerUp(ctx: MappedEventTarget): boolean {
    ctx.sourceTarget.click();
    return true;
  }
}

export class PointerSyntheticAdapter implements InteractionAdapter {
  name = 'PointerSyntheticAdapter';

  match(): boolean {
    return true;
  }

  pointerDown(ctx: MappedEventTarget): boolean {
    dispatchSyntheticEvent(ctx.sourceTarget, 'pointerdown');
    return true;
  }

  pointerMove(ctx: MappedEventTarget): boolean {
    dispatchSyntheticEvent(ctx.sourceTarget, 'pointermove');
    return true;
  }

  pointerUp(ctx: MappedEventTarget): boolean {
    dispatchSyntheticEvent(ctx.sourceTarget, 'pointerup');
    return true;
  }
}

export class TextInputProxyAdapter implements InteractionAdapter {
  name = 'TextInputProxyAdapter';

  match(el: HTMLElement): boolean {
    return el instanceof HTMLInputElement && ['text', 'search', 'email', 'url', 'tel', 'password'].includes(el.type);
  }

  pointerUp(ctx: MappedEventTarget): boolean {
    const sourceInput = ctx.sourceTarget as HTMLInputElement;
    const activationLayer = findActivationLayer(sourceInput);
    if (!activationLayer) return false;

    const proxy = sourceInput.cloneNode(false) as HTMLInputElement;
    proxy.removeAttribute('id');
    proxy.classList.add('ori-input-proxy');
    proxy.value = sourceInput.value;
    const localBox = readLocalBox(sourceInput);
    proxy.style.position = 'absolute';
    proxy.style.left = `${localBox?.x ?? 0}px`;
    proxy.style.top = `${localBox?.y ?? 0}px`;
    proxy.style.width = `${localBox?.width ?? sourceInput.getBoundingClientRect().width}px`;
    proxy.style.height = `${localBox?.height ?? sourceInput.getBoundingClientRect().height}px`;
    proxy.style.pointerEvents = 'auto';

    activationLayer.replaceChildren(proxy);

    const sync = () => {
      sourceInput.value = proxy.value;
      sourceInput.dispatchEvent(new Event('input', { bubbles: true }));
    };
    proxy.addEventListener('input', sync);
    proxy.addEventListener('blur', () => {
      sync();
      sourceInput.dispatchEvent(new Event('change', { bubbles: true }));
    }, { once: true });
    proxy.focus();
    proxy.setSelectionRange?.(proxy.value.length, proxy.value.length);
    return true;
  }
}

function findActivationLayer(element: HTMLElement): HTMLElement | null {
  const sourceLayer = element.closest('.ori-source-layer');
  return sourceLayer?.parentElement?.querySelector<HTMLElement>(':scope > .ori-activation-layer') ?? null;
}

function dispatchSyntheticEvent(target: HTMLElement, type: string): void {
  const EventCtor = typeof PointerEvent === 'function' ? PointerEvent : Event;
  target.dispatchEvent(new EventCtor(type, { bubbles: true }));
}

function applyAngleUpdates(foldOps: FoldOp[], updates: Array<{ opId: Id; angleDeg: number }>): boolean {
  let didUpdate = false;
  for (const update of updates) {
    const op = foldOps.find((candidate) => candidate.id === update.opId);
    if (!op) continue;
    op.angleDeg = update.angleDeg;
    didUpdate = true;
  }
  return didUpdate;
}

class StaticOrigamiView implements OrigamiRuntime {
  readonly mode = 'static-view';
  private state: OrigamiDocumentState;
  private tree: DerivedFoldTree;
  private renderer: FoldVisualRenderer;

  constructor(private readonly options: StaticViewRuntimeOptions) {
    this.state = {
      paper: options.paper,
      camera: defaultCamera(options.paper, options.camera),
      foldOps: cloneFoldOps(options.foldOps),
      controls: {}
    };
    this.tree = buildDerivedFoldTree(this.state);
    setupHost(options.host, 'static-view', this.state.camera);
    this.renderer = new FoldVisualRenderer(ensureLayer(options.host, 'ori-visual-layer'));
    this.renderer.setSnapshot(options.snapshot);
  }

  async mount(): Promise<void> {
    this.render();
  }

  render(): void {
    this.renderer.renderPieces(piecesFromTree(this.tree, this.state.paper), this.state.paper, false);
  }

  setAngle(opId: Id, angleDeg: number): boolean {
    return this.setAngles([{ opId, angleDeg }]);
  }

  setAngles(updates: Array<{ opId: Id; angleDeg: number }>): boolean {
    if (!applyAngleUpdates(this.state.foldOps, updates)) return false;
    this.tree = buildDerivedFoldTree(this.state);
    this.render();
    return true;
  }

  dispose(): void {
    this.options.host.innerHTML = '';
  }
}

class BakedOrigamiView implements OrigamiRuntime {
  readonly mode = 'baked-view';
  private renderer: FoldVisualRenderer;

  constructor(private readonly options: BakedViewRuntimeOptions) {
    setupHost(options.host, 'baked-view', options.manifest.camera);
    options.host.dataset.oriBaked = 'true';
    this.renderer = new FoldVisualRenderer(ensureLayer(options.host, 'ori-visual-layer'));
    this.renderer.setSnapshot(options.manifest.snapshot);
  }

  async mount(): Promise<void> {
    this.render();
  }

  render(): void {
    this.renderer.renderPieces(this.options.manifest.pieces, this.options.manifest.paper, true);
  }

  setAngle(): boolean {
    return false;
  }

  setAngles(): boolean {
    return false;
  }

  dispose(): void {
    this.options.host.innerHTML = '';
  }
}

class InteractiveOrigamiRuntime implements OrigamiRuntime {
  readonly mode = 'interactive-bridge';
  private readonly state: OrigamiDocumentState;
  private tree: DerivedFoldTree;
  private readonly source: SourceSurface;
  private readonly renderer: FoldVisualRenderer | LiveMirrorRenderer;
  private readonly keyRegistry = new FoldElementKeyRegistry();
  private readonly adapters: InteractionAdapter[];
  private readonly interactionLayer: HTMLElement;
  private snapshot: Snapshot | null = null;

  constructor(private readonly options: InteractiveRuntimeOptions) {
    this.state = {
      paper: options.paper,
      camera: defaultCamera(options.paper, options.camera),
      foldOps: cloneFoldOps(options.foldOps ?? []),
      controls: {}
    };
    this.tree = buildDerivedFoldTree(this.state);
    this.adapters = [...(options.adapters ?? []), new TextInputProxyAdapter(), new ButtonAdapter(), new AnchorAdapter()];
    this.source = new SourceSurface(options.sourceRoot);
    setupHost(options.host, 'interactive-bridge', this.state.camera);
    ensureLayer(options.host, 'ori-source-layer').appendChild(options.sourceRoot);
    const visualLayer = ensureLayer(options.host, 'ori-visual-layer');
    this.renderer = options.visual?.backend === 'live-mirror'
      ? new LiveMirrorRenderer(visualLayer, options.sourceRoot, this.keyRegistry)
      : new FoldVisualRenderer(visualLayer);
    this.interactionLayer = ensureLayer(options.host, 'ori-interaction-layer');
    ensureLayer(options.host, 'ori-activation-layer');
  }

  private onLayerPointer = (event: PointerEvent): void => {
    const rect = this.options.host.getBoundingClientRect();
    this.bridgePointer({
      clientX: event.clientX - rect.left,
      clientY: event.clientY - rect.top,
      type: event.type
    });
  };

  private onLayerPointerExit = (): void => {
    this.clearLivePseudoState();
  };

  async mount(): Promise<void> {
    this.snapshot = await this.options.snapshotProvider.capture(this.options.sourceRoot, this.state.paper);
    if (this.renderer instanceof FoldVisualRenderer) this.renderer.setSnapshot(this.snapshot);
    this.render();
    this.interactionLayer.addEventListener('pointerdown', this.onLayerPointer);
    this.interactionLayer.addEventListener('pointermove', this.onLayerPointer);
    this.interactionLayer.addEventListener('pointerup', this.onLayerPointer);
    this.interactionLayer.addEventListener('pointercancel', this.onLayerPointerExit);
    this.interactionLayer.addEventListener('pointerleave', this.onLayerPointerExit);
  }

  render(): void {
    if (!this.snapshot && this.renderer instanceof FoldVisualRenderer) return;
    this.renderer.renderPieces(piecesFromTree(this.tree, this.state.paper), this.state.paper, false);
    if (this.renderer instanceof LiveMirrorRenderer) this.renderer.mirrorFormValues();
  }

  setAngle(opId: Id, angleDeg: number): boolean {
    return this.setAngles([{ opId, angleDeg }]);
  }

  setAngles(updates: Array<{ opId: Id; angleDeg: number }>): boolean {
    if (!applyAngleUpdates(this.state.foldOps, updates)) return false;
    this.tree = buildDerivedFoldTree(this.state);
    this.render();
    return true;
  }

  setMode(mode: 'static-view' | 'interactive-bridge'): void {
    this.options.host.dataset.oriMode = mode;
  }

  bridgePointer(event: { clientX: number; clientY: number; type: string }): boolean {
    const hit = hitTestFoldTree({ x: event.clientX, y: event.clientY }, this.tree);
    if (!hit) return false;
    const target = this.source.elementFromLocalPoint(hit.localPoint);
    if (!target) return false;

    const ctx: MappedEventTarget = {
      originalEvent: event,
      hit,
      sourcePoint: hit.localPoint,
      sourceTarget: target,
      elementId: target.dataset.oriElementId
    };

    this.syncLivePseudoState(event.type, target);

    const method = event.type === 'pointerdown'
      ? 'pointerDown'
      : event.type === 'pointermove'
        ? 'pointerMove'
        : event.type === 'pointerup' || event.type === 'click'
          ? 'pointerUp'
          : undefined;

    if (method) {
      for (const adapter of this.adapters) {
        const handler = adapter[method];
        if (adapter.match(target) && handler?.call(adapter, ctx)) return true;
      }
    }

    dispatchSyntheticEvent(target, event.type);
    return true;
  }

  private syncLivePseudoState(type: string, target: HTMLElement): void {
    if (!(this.renderer instanceof LiveMirrorRenderer)) return;
    const pseudo = this.options.visual?.pseudoStates;
    if (!pseudo) return;
    const key = this.keyRegistry.ensureKey(target);
    if (type === 'pointermove' && pseudo.hover) this.renderer.setPseudoState({ key, hover: true });
    if (type === 'pointerdown' && pseudo.active) this.renderer.setPseudoState({ key, active: true });
    if ((type === 'pointerup' || type === 'click') && pseudo.active) this.renderer.setPseudoState({ active: false });
  }

  private clearLivePseudoState(): void {
    if (!(this.renderer instanceof LiveMirrorRenderer)) return;
    const pseudo = this.options.visual?.pseudoStates;
    if (!pseudo) return;
    this.renderer.setPseudoState({
      hover: pseudo.hover ? false : undefined,
      active: pseudo.active ? false : undefined,
      focus: pseudo.focus ? false : undefined,
      focusVisible: pseudo.focusVisible ? false : undefined
    });
  }

  dispose(): void {
    this.interactionLayer.removeEventListener('pointerdown', this.onLayerPointer);
    this.interactionLayer.removeEventListener('pointermove', this.onLayerPointer);
    this.interactionLayer.removeEventListener('pointerup', this.onLayerPointer);
    this.interactionLayer.removeEventListener('pointercancel', this.onLayerPointerExit);
    this.interactionLayer.removeEventListener('pointerleave', this.onLayerPointerExit);
    this.source.dispose();
    this.snapshot?.revoke?.();
    this.options.host.innerHTML = '';
  }
}

function setupHost(host: HTMLElement, mode: OrigamiRuntimeMode, camera: CameraSpec): void {
  host.dataset.oriMode = mode;
  host.style.position = host.style.position || 'relative';
  host.style.perspective = `${camera.perspective}px`;
  host.style.perspectiveOrigin = `${camera.perspectiveOrigin.x}px ${camera.perspectiveOrigin.y}px`;
  host.style.transformStyle = 'preserve-3d';
}

function ensureLayer(host: HTMLElement, className: string): HTMLElement {
  const existing = host.querySelector<HTMLElement>(`:scope > .${className}`);
  if (existing) return existing;
  const layer = document.createElement('div');
  layer.className = className;
  layer.style.position = 'absolute';
  layer.style.inset = '0';
  if (className === 'ori-visual-layer' || className === 'ori-activation-layer') layer.style.transformStyle = 'preserve-3d';
  if (className === 'ori-visual-layer' || className === 'ori-activation-layer') layer.style.pointerEvents = 'none';
  host.appendChild(layer);
  return layer;
}

export function createOrigamiRuntime(options: OrigamiRuntimeOptions): OrigamiRuntime {
  if (options.mode === 'static-view') return new StaticOrigamiView(options);
  if (options.mode === 'baked-view') return new BakedOrigamiView(options);
  return new InteractiveOrigamiRuntime(options);
}

export type FoldAxis = 'x' | 'y';

export interface Fold3DOptions {
  segments?: number;
  axis?: FoldAxis;
  angle?: number;
  perspective?: number | string;
  hideOriginal?: boolean;
}

export interface Fold3DNormalizedOptions {
  segments: number;
  axis: FoldAxis;
  angle: number;
  perspective: string;
  hideOriginal: boolean;
}

export interface Fold3DInstance {
  readonly element: HTMLElement;
  readonly options: Fold3DNormalizedOptions;
  setAngle(angle: number): void;
  destroy(): void;
}

const DEFAULT_FOLD3D_OPTIONS: Fold3DNormalizedOptions = {
  segments: 6,
  axis: 'y',
  angle: 0,
  perspective: '900px',
  hideOriginal: false
};

export function normalizeOptions(options: Fold3DOptions = {}): Fold3DNormalizedOptions {
  const merged = { ...DEFAULT_FOLD3D_OPTIONS, ...options };
  return {
    segments: Math.max(1, Math.floor(merged.segments)),
    axis: merged.axis,
    angle: merged.angle,
    perspective: typeof merged.perspective === 'number' ? `${merged.perspective}px` : merged.perspective,
    hideOriginal: merged.hideOriginal
  };
}

export function createFold3D(element: HTMLElement, options: Fold3DOptions = {}): Fold3DInstance {
  const normalized = normalizeOptions(options);
  const previousVisibility = element.style.visibility;
  element.dataset.fold3d = 'attached';
  element.style.setProperty('--fold3d-angle', `${normalized.angle}deg`);
  element.style.setProperty('--fold3d-perspective', normalized.perspective);
  if (normalized.hideOriginal) element.style.visibility = 'hidden';

  return {
    element,
    options: normalized,
    setAngle(angle: number) {
      element.style.setProperty('--fold3d-angle', `${angle}deg`);
    },
    destroy() {
      delete element.dataset.fold3d;
      element.style.removeProperty('--fold3d-angle');
      element.style.removeProperty('--fold3d-perspective');
      element.style.visibility = previousVisibility;
    }
  };
}
